import type { PantryNavApiResponse } from '../types/pantryNavigation'

const CACHE_VERSION = 2
const KEY_PREFIX = `bs.pantry-nav.v${CACHE_VERSION}.`

interface CachedEnvelope {
  v: number
  apiVersion?: number
  data: PantryNavApiResponse
  cachedAt: number
}

const storageKey = (storeId: string) => `${KEY_PREFIX}${storeId}`

const safeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    return window.localStorage
  } catch {
    return null
  }
}

export const readNavCache = (
  storeId: string | undefined,
): PantryNavApiResponse | null => {
  if (!storeId) return null
  const storage = safeLocalStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(storageKey(storeId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedEnvelope | null
    if (!parsed || parsed.v !== CACHE_VERSION || !parsed.data) return null
    if (!Array.isArray(parsed.data.sections)) return null
    return parsed.data
  } catch {
    return null
  }
}

export const writeNavCache = (
  storeId: string | undefined,
  data: PantryNavApiResponse | null | undefined,
) => {
  if (!storeId || !data) return
  const storage = safeLocalStorage()
  if (!storage) return
  try {
    const envelope: CachedEnvelope = {
      v: CACHE_VERSION,
      apiVersion: data.version,
      data,
      cachedAt: Date.now(),
    }
    storage.setItem(storageKey(storeId), JSON.stringify(envelope))
  } catch {
    // localStorage may be unavailable (quota exceeded / privacy mode) — swallow
  }
}

export const clearNavCache = (storeId?: string) => {
  const storage = safeLocalStorage()
  if (!storage) return
  try {
    if (storeId) {
      storage.removeItem(storageKey(storeId))
      return
    }
    const keys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i)
      if (k && k.startsWith(KEY_PREFIX)) keys.push(k)
    }
    keys.forEach((k) => storage.removeItem(k))
  } catch {
    // ignore
  }
}

export const PANTRY_NAV_CACHE_KEY_PREFIX = KEY_PREFIX
export const PANTRY_NAV_CACHE_VERSION = CACHE_VERSION
